import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { FacebookIcon, MessageCircleIcon, SendIcon, YoutubeIcon, CheckCircleIcon, PlugIcon, InstagramIcon } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { showSuccess, showError } from '@/lib/toast-helpers'

interface InstagramAccount {
  business_type: string
  username: string
  account_type: string
  profile_picture_url: string | null
  token_expires_at: string
  conversations_count: number
  instagram_user_id: string
  webhook_recipient_id: string
}

interface Integration {
  id: string
  name: string
  description: string
  connected: boolean
  credentials: {
    [key: string]: string | null
  }
  accounts?: InstagramAccount[]
  available_slots?: number
}

interface IntegrationsIndexProps {
  integrations: Integration[]
}

export default function IntegrationsIndex({ integrations }: IntegrationsIndexProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null)
  const [formData, setFormData] = useState<{ [key: string]: string }>({})
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  // Listen for OAuth callback messages from popup
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) return

      if (event.data.type === 'instagram_oauth_callback') {
        if (event.data.success) {
          showSuccess(event.data.message)
          // Reload the page to show updated integration status
          router.reload()
        } else {
          showError(event.data.message)
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  const getIcon = (id: string) => {
    switch (id) {
      case 'instagram':
        return InstagramIcon
      case 'facebook_messenger':
        return FacebookIcon
      case 'whatsapp':
        return MessageCircleIcon
      case 'telegram':
        return SendIcon
      case 'youtube':
        return YoutubeIcon
      default:
        return PlugIcon
    }
  }

  const getIconColor = (id: string) => {
    switch (id) {
      case 'instagram':
        return 'text-pink-600'
      case 'facebook_messenger':
        return 'text-blue-600'
      case 'whatsapp':
        return 'text-green-600'
      case 'telegram':
        return 'text-sky-600'
      case 'youtube':
        return 'text-red-600'
      default:
        return 'text-gray-600'
    }
  }

  const getFormFields = (id: string): Array<{ name: string; label: string; placeholder: string; required: boolean }> => {
    switch (id) {
      case 'facebook_messenger':
        return [
          { name: 'page_id', label: 'Page ID', placeholder: 'Your Facebook Page ID', required: true },
          { name: 'access_token', label: 'Access Token', placeholder: 'Enter your Facebook access token', required: true },
        ]
      case 'whatsapp':
        return [
          { name: 'phone_number', label: 'Business Phone Number', placeholder: '+1234567890', required: true },
          { name: 'api_token', label: 'API Token', placeholder: 'Enter your WhatsApp Business API token', required: true },
        ]
      case 'telegram':
        return [
          { name: 'bot_token', label: 'Bot Token', placeholder: 'Enter your Telegram bot token', required: true },
          { name: 'chat_id', label: 'Chat ID', placeholder: 'Enter your chat ID', required: true },
        ]
      case 'youtube':
        return [
          { name: 'channel_id', label: 'Channel ID', placeholder: 'Your YouTube channel ID', required: true },
          { name: 'api_key', label: 'API Key', placeholder: 'Enter your YouTube API key', required: true },
        ]
      default:
        return []
    }
  }

  const handleConnect = async (integration: Integration, businessType?: string) => {
    // Instagram uses OAuth - open popup instead of credentials dialog
    if (integration.id === 'instagram') {
      try {
        // Fetch OAuth URL from backend with business_type parameter
        const url = businessType
          ? `/instagram/oauth/authorize?business_type=${businessType}`
          : '/instagram/oauth/authorize'

        const response = await fetch(url, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          }
        })
        const data = await response.json()

        // Open OAuth popup
        const width = 600
        const height = 700
        const left = window.screen.width / 2 - width / 2
        const top = window.screen.height / 2 - height / 2

        window.open(
          data.oauth_url,
          'instagram_oauth',
          `width=${width},height=${height},left=${left},top=${top}`
        )
      } catch (error) {
        showError('Failed to start Instagram authorization')
      }
      return
    }

    // Other integrations use credentials dialog
    setSelectedIntegration(integration)
    setFormData({})
    setErrors({})
    setDialogOpen(true)
  }

  const handleDisconnect = (integration: Integration, businessType?: string) => {
    router.post('/integrations/disconnect', {
      platform: integration.id,
      business_type: businessType
    })
  }

  const validateForm = (): boolean => {
    if (!selectedIntegration) return false

    const fields = getFormFields(selectedIntegration.id)
    const newErrors: { [key: string]: string } = {}

    fields.forEach(field => {
      if (field.required && !formData[field.name]?.trim()) {
        newErrors[field.name] = `${field.label} is required`
      }
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = () => {
    if (!validateForm() || !selectedIntegration) return

    router.post('/integrations/connect',
      {
        platform: selectedIntegration.id,
        credentials: formData
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          showSuccess(`${selectedIntegration.name} has been connected successfully.`)
          setDialogOpen(false)
          setSelectedIntegration(null)
          setFormData({})
        },
        onError: (errors) => {
          if (errors && typeof errors === 'object') {
            showError(Object.values(errors).flat().join(', '))
          } else {
            showError('Failed to connect integration')
          }
        }
      }
    )
  }

  return (
    <>
      <div className="flex flex-1 flex-col min-w-0">
        <div className="@container/main flex flex-1 flex-col gap-2 min-w-0">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 min-w-0">
            {/* Header */}
            <div className="px-4 lg:px-6">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Integrations</h1>
                <p className="text-muted-foreground">Connect your social media accounts to manage leads from multiple channels</p>
              </div>
            </div>

            {/* Integrations Grid */}
            <div className="px-4 lg:px-6">
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {integrations.map((integration) => {
                  const Icon = getIcon(integration.id)
                  const iconColor = getIconColor(integration.id)

                  // Special rendering for Instagram (supports multiple accounts)
                  if (integration.id === 'instagram' && integration.accounts && integration.accounts.length > 0) {
                    return (
                      <Card key={integration.id} className="relative md:col-span-2 lg:col-span-3" data-testid={`integration-card-${integration.id}`}>
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`flex size-12 items-center justify-center rounded-lg bg-muted`}>
                                <Icon className={`size-6 ${iconColor}`} />
                              </div>
                              <div>
                                <CardTitle>{integration.name}</CardTitle>
                                <Badge variant="outline" className="mt-1 text-green-600 border-green-600">
                                  <CheckCircleIcon className="mr-1 size-3" />
                                  {integration.accounts.length} account{integration.accounts.length > 1 ? 's' : ''} connected
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <CardDescription className="mb-4">
                            {integration.description}
                          </CardDescription>

                          {/* Connected Accounts */}
                          <div className="space-y-4">
                            {integration.accounts.map((account) => (
                              <div key={account.business_type} className="border rounded-lg p-4 space-y-3">
                                <div className="flex items-start justify-between">
                                  <div className="flex items-center gap-3">
                                    {account.profile_picture_url && (
                                      <img
                                        src={account.profile_picture_url}
                                        alt={account.username}
                                        className="size-12 rounded-full"
                                      />
                                    )}
                                    <div>
                                      <p className="font-semibold">@{account.username}</p>
                                      <p className="text-sm text-muted-foreground capitalize">
                                        {account.business_type} • {account.account_type}
                                      </p>
                                    </div>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDisconnect(integration, account.business_type)}
                                    data-testid={`disconnect-button-${account.business_type}`}
                                  >
                                    Disconnect
                                  </Button>
                                </div>

                                {/* Account Details */}
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <p className="text-muted-foreground">Conversations</p>
                                    <p className="font-medium">{account.conversations_count}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">Token Expires</p>
                                    <p className="font-medium">{new Date(account.token_expires_at).toLocaleDateString()}</p>
                                  </div>
                                  <div className="col-span-2">
                                    <p className="text-muted-foreground">User ID</p>
                                    <p className="font-mono text-xs">{account.instagram_user_id}</p>
                                  </div>
                                  <div className="col-span-2">
                                    <p className="text-muted-foreground">Webhook Recipient ID</p>
                                    <p className="font-mono text-xs">{account.webhook_recipient_id}</p>
                                  </div>
                                </div>
                              </div>
                            ))}

                            {/* Add Another Account Button */}
                            {integration.available_slots && integration.available_slots > 0 && (
                              <div className="pt-2">
                                <p className="text-sm text-muted-foreground mb-3">
                                  You can connect {integration.available_slots} more account{integration.available_slots > 1 ? 's' : ''} ({integration.available_slots === 2 ? 'shop and recruiting' : integration.accounts[0].business_type === 'shop' ? 'recruiting' : 'shop'})
                                </p>
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    const nextType = integration.accounts[0].business_type === 'shop' ? 'recruiting' : 'shop'
                                    handleConnect(integration, nextType)
                                  }}
                                  className="w-full bg-[#84b9ef] hover:bg-[#6fa8e0]"
                                  data-testid="connect-additional-instagram"
                                >
                                  Connect {integration.accounts[0].business_type === 'shop' ? 'Recruiting' : 'Shop'} Account
                                </Button>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )
                  }

                  // Standard rendering for other integrations
                  return (
                    <Card key={integration.id} className="relative" data-testid={`integration-card-${integration.id}`}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`flex size-12 items-center justify-center rounded-lg bg-muted`}>
                              <Icon className={`size-6 ${iconColor}`} />
                            </div>
                            <div>
                              <CardTitle>{integration.name}</CardTitle>
                              {integration.connected && (
                                <Badge variant="outline" className="mt-1 text-green-600 border-green-600">
                                  <CheckCircleIcon className="mr-1 size-3" />
                                  Connected
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="mb-4">
                          {integration.description}
                        </CardDescription>
                        {integration.connected ? (
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDisconnect(integration)}
                              className="w-full"
                              data-testid={`disconnect-button-${integration.id}`}
                            >
                              Disconnect
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => handleConnect(integration)}
                            className="w-full bg-[#84b9ef] hover:bg-[#6fa8e0]"
                            data-testid={`connect-button-${integration.id}`}
                          >
                            Connect
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Connection Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="connection-dialog">
          <DialogHeader>
            <DialogTitle>Connect {selectedIntegration?.name}</DialogTitle>
            <DialogDescription>
              Enter your {selectedIntegration?.name} credentials to connect your account
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {selectedIntegration && getFormFields(selectedIntegration.id).map((field) => (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.name}>
                  {field.label} {field.required && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id={field.name}
                  placeholder={field.placeholder}
                  value={formData[field.name] || ''}
                  onChange={(e) => {
                    setFormData({ ...formData, [field.name]: e.target.value })
                    if (errors[field.name]) {
                      setErrors({ ...errors, [field.name]: '' })
                    }
                  }}
                  data-testid={`input-${field.name}`}
                />
                {errors[field.name] && (
                  <p className="text-sm text-destructive">{errors[field.name]}</p>
                )}
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-[#84b9ef] hover:bg-[#6fa8e0]"
              data-testid="submit-button"
            >
              Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  )
}
